import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import { KeyRound, ShieldCheck, ArrowRight, ExternalLink, Sparkles, UserCheck, Zap, AlertCircle } from 'lucide-react';

export interface AuthGatewayNodeData {
  authType?: 'login_and_register' | 'login_only' | 'register_only';
  triggerIntent?: string;
  successRedirect?: string;
  modalTitle?: string;
  enableVoiceTrigger?: boolean;
  isCustomizable?: boolean;
  [key: string]: unknown;
}

export const AuthGatewayNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as AuthGatewayNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData } = useTenantView();

  const persistNodeDataDirectly = (field: keyof AuthGatewayNodeData, value: any, allUpdates?: Partial<AuthGatewayNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const res = await fetch('/api/admin/luna-flows/save-node-data', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'AUTH_GATEWAY',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[AuthGatewayNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof AuthGatewayNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);
  const enableVoiceTrigger = nodeData.enableVoiceTrigger !== false;

  return (
    <div
      className={`group rounded-2xl border transition-all duration-300 w-[380px] select-none relative backdrop-blur-xl bg-gradient-to-b from-[#081813]/98 via-[#05100D]/98 to-[#030806]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-emerald-400 ring-2 ring-emerald-400/50 shadow-[0_0_35px_rgba(16,185,129,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-emerald-500/40 hover:border-emerald-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(16,185,129,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View Live Data in Portal (Fades in on Hover) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('authGatewayNode', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-950 via-slate-900 to-teal-950 border-2 border-emerald-400 text-emerald-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(16,185,129,0.8)] hover:border-emerald-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data to configure security and auth gateway settings"
          >
            <KeyRound size={13} className="text-emerald-300 animate-pulse" />
            <span>View Data in Portal</span>
            <ArrowRight size={13} className="text-emerald-400" />
          </button>
        </div>
      )}

      {/* ── Directional Connection Handles (Left in from Luna Core / Right out) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-auth"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Input from Luna Core Hub (out-actions)"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-auth"
        className="!bg-purple-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(168,85,247,1)]"
        title="Output: Subsequent Actions Post-Authentication"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-purple-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(168,85,247,0.8)]"
      />

      {/* ── Node Header ──────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-emerald-950/80 via-teal-950/60 to-slate-950/80 px-4 py-3 border-b border-emerald-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.3)]">
            <KeyRound size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">Auth Gateway & Login</h4>
            <p className="text-[10px] text-emerald-300/70 font-mono">Zero-Trust Portal Bridge</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-emerald-400 animate-pulse bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
            AUTH GATEWAY
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      {/* ── Node Body & Parameter Inputs ──────────────────────────────── */}
      <div className="p-4 space-y-3.5 text-xs">
        
        {/* Authentication Mode Selector */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-between">
            <span>Auth Capabilities</span>
            <span className="text-emerald-400 text-[9px]">ZERO-TRUST</span>
          </label>
          <select
            value={nodeData.authType || 'login_and_register'}
            onChange={(e) => handleChange('authType', e.target.value)}
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-2 text-slate-100 text-xs font-medium focus:outline-none focus:border-emerald-400 transition"
          >
            <option value="login_and_register">Sign In & Self-Registration</option>
            <option value="login_only">Sign In Only (Invite-Only Clients)</option>
            <option value="register_only">Registration Onboarding Only</option>
          </select>
        </div>

        {/* Natural Language Trigger Intent */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-between">
            <span>Trigger Intent / AI Condition</span>
            <span className="text-emerald-400 text-[9px]">LUNA HOOK</span>
          </label>
          <textarea
            rows={2}
            value={nodeData.triggerIntent || ''}
            onChange={(e) => handleChange('triggerIntent', e.target.value)}
            placeholder="e.g. User asks to sign in, check tax return status, access bank balance, or open client portal"
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-emerald-400 transition resize-none leading-relaxed"
          />
        </div>

        {/* Modal Window Title */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold">
            Widget Display Title
          </label>
          <input
            type="text"
            value={nodeData.modalTitle || ''}
            onChange={(e) => handleChange('modalTitle', e.target.value)}
            placeholder="Client Portal Security Gateway"
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-1.5 text-slate-100 text-xs focus:outline-none focus:border-emerald-400 transition"
          />
        </div>

        {/* Success Redirect URL Target */}
        <div className="space-y-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-semibold flex items-center justify-between">
            <span>Post-Auth Destination</span>
            <span className="text-cyan-400 text-[9px] font-mono">PORTAL URL</span>
          </label>
          <input
            type="text"
            value={nodeData.successRedirect || ''}
            onChange={(e) => handleChange('successRedirect', e.target.value)}
            placeholder="/portal/{{tenantId}}"
            className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3 py-1.5 text-cyan-200 text-xs font-mono focus:outline-none focus:border-emerald-400 transition"
          />
        </div>

        {/* Voice Trigger Capability Toggle */}
        <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <UserCheck size={12} className="text-emerald-400" />
            <span className="text-[10px] text-slate-300 font-medium">Autonomous Voice Trigger (`open_auth_widget`)</span>
          </div>
          <input
            type="checkbox"
            checked={enableVoiceTrigger}
            onChange={(e) => handleChange('enableVoiceTrigger', e.target.checked)}
            className="rounded border-slate-700 text-emerald-500 focus:ring-emerald-400 cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
});

AuthGatewayNode.displayName = 'AuthGatewayNode';
export default AuthGatewayNode;
